import { catalogNameToFhir, getTRPCClient, PROTOCOL_DEFINITION_KIND, syncKey } from "@repo/api";
import {
  type ChangeProtocolDefinitionAvailabilityInput,
  type ClearProtocolValuesInput,
  type CorrectProtocolDefinitionInput,
  changeProtocolDefinitionAvailabilityInputSchema,
  clearProtocolValuesInputSchema,
  correctProtocolDefinitionInputSchema,
  type DefineProtocolDefinitionInput,
  type DefineProtocolDefinitionParams,
  defineProtocolDefinitionInputSchema,
  type ReviseProtocolDefinitionInput,
  type ReviseProtocolDefinitionParams,
  reviseProtocolDefinitionInputSchema,
} from "@repo/api/schemas";
import {
  asMemberId,
  asPlannedTrialId,
  examRef,
  mintEntityId,
  type ProtocolDefinitionProps,
  procedureRef,
  resolvePlacement,
  type StepPlacementProps,
} from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, inArray, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { writeProtocolDefinitionRow } from "../fhir/protocol-definition-storage";
import type { PlanDefinitionRow } from "../fhir/types";
import {
  type ProtocolDefinitionProjectionFailure,
  projectProtocolDefinitionRow,
  projectProtocolDefinitionRows,
} from "./protocol-definition-projection";
import { PINNED_PROTOCOL_KINDS } from "./shared/protocol-kinds";

export type { ProtocolDefinitionProjectionFailure } from "./protocol-definition-projection";

const PLAN_DEFINITION_COLLECTION = "planDefinition";
/** Every protocol canonical this app mints sits directly under this base. */
export const PROTOCOL_DEFINITION_CANONICAL_BASE = canonicalBase("PlanDefinition");

export type ProtocolDefinitionAvailability = "Active" | "Inactive";

export type ProtocolAnswerValueDto =
  | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
  | { kind: "Integer"; value: number }
  | { kind: "Choice"; selected: readonly string[]; freeText?: string }
  | { kind: "Boolean"; value: boolean }
  | { kind: "Text"; value: string };

export interface ProtocolParameterAnswerDto {
  source: "self" | string;
  parameterId: string;
  value: ProtocolAnswerValueDto;
}

export interface ProtocolPlannedTrialDto {
  id: string;
  values: readonly ProtocolParameterAnswerDto[];
}

export interface ProtocolOccurrenceDto {
  /** Persisted provenance; absent on newly authored optimistic occurrences. */
  justifications?: readonly ({ kind: "authored" } | { kind: "exam"; exam: string })[];
  occurrenceId: string;
  definitionUrl: string;
  qualifier?: string;
  parameterValues: readonly ProtocolParameterAnswerDto[];
  observationLevels?: readonly import("@repo/domain").ObservationLevel[];
  /** Absent when none. An occurrence with planned trials has no `parameterValues`. */
  plannedTrials?: readonly ProtocolPlannedTrialDto[];
}

export interface ProtocolStepDto {
  placement: StepPlacementProps;
  title?: string;
  occurrences: readonly ProtocolOccurrenceDto[];
  durationMinutes: number;
}

/** UI read model for redesigned protocol authoring and detail workflows. */
export interface ProtocolDefinitionDto {
  definitionUrl: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: ProtocolDefinitionAvailability;
  steps: readonly ProtocolStepDto[];
  examDefinitionUrls: readonly string[];
  envelopeMinutes: number;
  totalDurationMinutes: number;
}

export interface ProtocolDefinitionFilters {
  availability?: ProtocolDefinitionAvailability;
  search?: string;
  /** Exact immutable revisions required by an aggregate read model. */
  canonicals?: readonly string[];
}

const planDefinitions = () => getDataStoreClient().getCollection<PlanDefinitionRow, string>(PLAN_DEFINITION_COLLECTION);

export function mintProtocolDefinitionUrl(): string {
  return canonicalUrl("PlanDefinition", mintEntityId());
}

export function useProtocolDefinition(definitionUrl: string | undefined): ProtocolDefinitionDto | undefined {
  const collection = useCollection(PLAN_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "protocol-definition", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) =>
            and(eq(definition.catalogModelKind, PROTOCOL_DEFINITION_KIND), eq(definition.url, definitionUrl)),
          )
          .orderBy(({ definition }) => definition.catalogVersion, "desc")
          // Nested PlanDefinition actions and extensions have no declarative query expression.
          .fn.select(({ definition }) => projectProtocolDefinitionRow(definition))
          .findOne()
      );
    },
  });
  return result.data;
}

export interface ProtocolDefinitionsResult {
  definitions: ProtocolDefinitionDto[];
  failures: ProtocolDefinitionProjectionFailure[];
  isLoading: boolean;
  isReady: boolean;
}

export function useProtocolDefinitionsResult(filters: ProtocolDefinitionFilters = {}): ProtocolDefinitionsResult {
  const collection = useCollection(PLAN_DEFINITION_COLLECTION);
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const canonicals = filters.canonicals;
  const result = useLiveQuery({
    queryKey: [
      collection.id,
      "protocol-definitions",
      status ?? null,
      search ?? null,
      canonicals?.length ?? -1,
      ...(canonicals ?? []),
    ],
    query: (query) => {
      if (canonicals) {
        if (canonicals.length === 0) return undefined;
        let exact = query
          .from({ definition: collection })
          .where(({ definition }) => inArray(definition.catalogModelKind, PINNED_PROTOCOL_KINDS))
          .where(({ definition }) =>
            canonicals.length === 1
              ? eq(definition.definitionCanonical, canonicals[0] as string)
              : inArray(definition.definitionCanonical, [...canonicals]),
          );
        if (status) exact = exact.where(({ definition }) => eq(definition.status, status));
        if (search) {
          exact = exact.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          );
        }
        return exact
          .orderBy(({ definition }) => definition.title, "asc")
          .select(({ definition }) => ({ ...definition }));
      }
      const latestVersions = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, PROTOCOL_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      const current = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(eq(definition.catalogModelKind, PROTOCOL_DEFINITION_KIND), eq(definition.catalogVersion, latest.version)),
        );
      const available = status ? current.where(({ definition }) => eq(definition.status, status)) : current;
      const matching = search
        ? available.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          )
        : available;
      return matching
        .orderBy(({ definition }) => definition.title, "asc")
        .select(({ definition }) => ({ ...definition }));
    },
  });
  const projection = projectProtocolDefinitionRows((result.data as PlanDefinitionRow[] | undefined) ?? []);
  return { ...projection, isLoading: result.isLoading, isReady: result.isReady };
}

export function useProtocolDefinitions(filters: ProtocolDefinitionFilters = {}): ProtocolDefinitionDto[] {
  return useProtocolDefinitionsResult(filters).definitions;
}

function optimisticId(url: string, version: number): string {
  let hash = 2166136261;
  for (const character of url) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `optimistic-protocol-${(hash >>> 0).toString(36)}-${version}`;
}

/**
 * The authored input as the aggregate's own props, with every placement
 * resolved and every occurrence id settled — the same shape
 * `readProtocolDefinition` recovers from a synced row, which is what lets the
 * optimistic row read back unchanged.
 *
 * Placement is resolved HERE and not left implicit: a step whose duration the
 * author left open has no position in the visit until the occurrences settle
 * it, and the server resolves it the same way.
 */
function toProps(
  input: DefineProtocolDefinitionInput | ReviseProtocolDefinitionInput,
  spine: ProtocolDefinitionProps["spine"],
): ProtocolDefinitionProps {
  let occurrenceIndex = 0;
  return {
    spine,
    envelopeMinutes: input.envelopeMinutes,
    exams: input.exams.map(examRef),
    steps: input.steps.map((step) => {
      const placement = resolvePlacement(step.placement);
      return {
        ...(step.title === undefined ? {} : { title: step.title }),
        placement: { offsetMinutes: placement.offsetMinutes, durationMinutes: placement.durationMinutes },
        occurrences: step.occurrences.map((occurrence) => ({
          id: asMemberId(occurrence.id ?? `occ-${++occurrenceIndex}`),
          definition: procedureRef(occurrence.definition),
          ...(occurrence.qualifier === undefined ? {} : { qualifier: occurrence.qualifier }),

          justifications: [{ kind: "authored" as const }],
          parameterValues: occurrence.parameterValues.map(parameterAnswer),
          ...(occurrence.observationLevels?.length
            ? {
                observationLevels: occurrence.observationLevels.map((entry) => ({
                  observationId: asMemberId(entry.observationId),
                  level: entry.level,
                })),
              }
            : {}),
          ...(occurrence.plannedTrials?.length
            ? {
                plannedTrials: occurrence.plannedTrials.map((trial) => ({
                  id: asPlannedTrialId(trial.id),
                  values: trial.values.map(parameterAnswer),
                })),
              }
            : {}),
        })),
      };
    }),
  };
}

function parameterAnswer(
  answer: DefineProtocolDefinitionInput["steps"][number]["occurrences"][number]["parameterValues"][number],
) {
  return {
    // `self` is the sentinel for "this occurrence's own parameter"; a
    // member id is anything else, and the two cannot collide.
    source: answer.source === "self" ? ("self" as const) : asMemberId(answer.source),
    parameterId: asMemberId(answer.parameterId),
    value:
      answer.value.kind === "Choice"
        ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
        : { ...answer.value },
  };
}

/**
 * A REAL `PlanDefinition`, in the shape the engine's encoder writes, plus the
 * derived columns an insert bypasses (`deriveFields` runs at sync only). The
 * projection reads it exactly as it reads the synced row that replaces it.
 */
function optimisticRow(props: ProtocolDefinitionProps): PlanDefinitionRow {
  return {
    ...writeProtocolDefinitionRow(props),
    id: optimisticId(props.spine.url, props.spine.version),
    catalogModelKind: PROTOCOL_DEFINITION_KIND,
    catalogVersion: props.spine.version,
    definitionCanonical: `${props.spine.url}|${props.spine.version}`,
    catalogViewScope: "catalog-definitions",
  };
}

function currentRow(definitionUrl: string): PlanDefinitionRow & { id: string } {
  const row = [...planDefinitions().state.values()]
    .filter((candidate) => candidate.url === definitionUrl && candidate.catalogModelKind === PROTOCOL_DEFINITION_KIND)
    .sort((left, right) => right.catalogVersion - left.catalogVersion)[0];
  if (!row?.id) throw new Error(`Protocol Definition '${definitionUrl}' is not available in the data store`);
  return { ...row, id: row.id };
}

export const defineProtocolDefinition = defineWrite<DefineProtocolDefinitionParams>({
  optimistic: (params) => {
    const input = defineProtocolDefinitionInputSchema.parse(params);
    planDefinitions().insert(
      optimisticRow(
        toProps(input, {
          url: input.definitionUrl,
          version: 1,
          mintedAt: new Date().toISOString(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          availability: "Active",
        }),
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.protocolDefinition.define.mutate(input, options);
  },
});

export const reviseProtocolDefinition = defineWrite<ReviseProtocolDefinitionParams>({
  optimistic: (params) => {
    const input = reviseProtocolDefinitionInputSchema.parse(params);
    const current = projectProtocolDefinitionRow(currentRow(input.definitionUrl));
    planDefinitions().insert(
      optimisticRow(
        toProps(input, {
          url: current.definitionUrl,
          version: input.expectedVersion + 1,
          mintedAt: new Date().toISOString(),
          name: current.name,
          ...(current.description === undefined ? {} : { description: current.description }),
          availability: current.availability,
        }),
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.protocolDefinition.revise.mutate(input, options);
  },
});

export const correctProtocolDefinition = defineWrite<CorrectProtocolDefinitionInput>({
  optimistic: (params) => {
    const input = correctProtocolDefinitionInputSchema.parse(params);
    const row = currentRow(input.definitionUrl);
    planDefinitions().update(syncKey(row), (draft) => {
      if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
      if (input.description === null) delete draft.description;
      else if (input.description !== undefined) draft.description = input.description;
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.protocolDefinition.correct.mutate(input, options);
  },
});

/**
 * The new revision arrives through sync. The optimistic write only touches the
 * current row, because a transaction with no tracked write never calls the server.
 */
export const clearProtocolValues = defineWrite<ClearProtocolValuesInput>({
  optimistic: (params) => {
    const input = clearProtocolValuesInputSchema.parse(params);
    planDefinitions().update(syncKey(currentRow(input.definitionUrl)), (draft) => {
      draft.meta = { ...draft.meta, lastUpdated: new Date().toISOString() };
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.protocolDefinition.clearValues.mutate(input, options);
  },
});

function availabilityAction(availability: ProtocolDefinitionAvailability) {
  return defineWrite<ChangeProtocolDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeProtocolDefinitionAvailabilityInputSchema.parse(params);
      const row = currentRow(input.definitionUrl);
      planDefinitions().update(syncKey(row), (draft) => {
        draft.status = availability === "Active" ? "active" : "retired";
      });
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.protocolDefinition.activate
          : getTRPCClient().catalog.protocolDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateProtocolDefinition = availabilityAction("Active");
export const deactivateProtocolDefinition = availabilityAction("Inactive");
